// TSPL2 label builder for TSC-style USB barcode printers (the Android app's
// "Connects to only TSC USB Barcode Printer"). Produces the same label the
// Barcode Printing preview shows, as printer commands streamed over the same
// USB transport as receipts (usbPrinter.printBytes).

// 203 dpi label printers: 8 dots per mm.
const DOTS_PER_MM = 8;

// TSPL strings are quoted ASCII; drop quotes and non-ASCII (₹ → Rs).
function clean(s: string): string {
  return s
    .replace(/₹/g, 'Rs')
    .replace(/"/g, "'")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, '')
    .trim();
}

// One "Title: Value" line on the label. `bold`/`scale` come from the Barcode
// Settings emphasis options (used for a big, bold Price line).
export interface LabelTextRow {
  label: string;
  value: string;
  bold?: boolean;
  /** Font size multiplier, 1 = normal. */
  scale?: 1 | 2 | 3;
}

export interface LabelSpec {
  widthMm: number;
  heightMm: number;
  // Barcode above ('Top') or below ('Bottom') the text block.
  position: 'Top' | 'Bottom';
  header?: string; // shop name
  name?: string; // product name
  rows: LabelTextRow[]; // enabled content rows (Price, MRP, HSN …)
  footer?: string; // "price with outlet" line
  code: string; // barcode value (CODE128)
  copies: number;
  /** Blank space at the top / left edge of the label, in mm. */
  marginTopMm?: number;
  marginLeftMm?: number;
  /** Blank space kept at the bottom of the label, in mm. */
  marginBottomMm?: number;
  /** Blank space kept at the right edge of the label, in mm. */
  marginRightMm?: number;
  /** Labels side by side across the roll (2-up stock). Default 1. */
  across?: number;
  /** Gap between those columns, in mm. */
  columnGapMm?: number;
  /** Vertical gap between labels on the roll, in mm (TSPL GAP). */
  gapMm?: number;
}

export function buildLabelTspl(spec: LabelSpec): Uint8Array {
  const H = spec.heightMm * DOTS_PER_MM;
  // Configured margins (mm → dots), with a small default so nothing sits hard
  // against the label edge.
  const marginTop = Math.round((spec.marginTopMm ?? 1) * DOTS_PER_MM);
  const margin = Math.round((spec.marginLeftMm ?? 1) * DOTS_PER_MM);
  // Characters that fit across the label at a given font/multiplier — used to
  // keep an emphasised (big) Price line from running off the edge.
  const marginRight = Math.round((spec.marginRightMm ?? 0) * DOTS_PER_MM);
  const usableDots = spec.widthMm * DOTS_PER_MM - margin - marginRight;
  const fits = (text: string, fontDots: number, mul: number) =>
    text.length * fontDots * mul <= usableDots;

  // Text lines, top to bottom, with their TSPL font and line height in dots.
  interface Line {
    text: string;
    font: string;
    height: number;
    scale: number;
  }
  const lines: Line[] = [];
  if (spec.header) lines.push({ text: clean(spec.header), font: '2', height: 24, scale: 1 });
  if (spec.name) lines.push({ text: clean(spec.name), font: '2', height: 24, scale: 1 });
  spec.rows.forEach((r) => {
    // TSPL has no bold attribute; a heavier/larger built-in font stands in for
    // it, and `scale` becomes the TEXT x/y multiplier. A line that would not
    // fit the label width drops its title, then shrinks — so a big Price prints
    // in full instead of being cut off.
    const font = r.bold ? '3' : '1';
    const fontDots = r.bold ? 12 : 8;
    let scale = r.scale ?? 1;
    let text = clean(`${r.label}: ${r.value}`);
    if (!fits(text, fontDots, scale)) text = clean(r.value);
    while (scale > 1 && !fits(text, fontDots, scale)) scale -= 1;
    lines.push({ text, font, scale, height: (r.bold ? 24 : 16) * scale });
  });
  if (spec.footer) lines.push({ text: clean(spec.footer), font: '1', height: 16, scale: 1 });

  // ---- Fit the block to the label height ----
  // A 15mm label is only 120 dots tall, so header + name + rows + bars can
  // easily overrun it — everything past the edge is simply lost by the printer
  // (which is how the Price line came out sliced in half). Reclaim space in
  // steps until the whole block fits, rather than letting it overflow.
  const bottomPad = Math.round((spec.marginBottomMm ?? 0) * DOTS_PER_MM);
  const available = Math.max(48, H - marginTop - bottomPad);
  const MIN_BAR = 24;
  const MAX_BAR = 80;
  const readableH = 18;
  let hri = true; // human-readable digits under the bars

  const textH = () => lines.reduce((a, l) => a + l.height, 0);
  const needed = () => textH() + MIN_BAR + (hri ? readableH : 0);

  const shrinkSteps: (() => void)[] = [
    // 1. give up the Price emphasis multiplier
    () =>
      lines.forEach((l) => {
        if (l.scale > 1) {
          l.height = Math.round(l.height / l.scale);
          l.scale = 1;
        }
      }),
    // 2. drop every line to the smallest font
    () =>
      lines.forEach((l) => {
        if (l.font !== '1') {
          l.font = '1';
          l.height = 16;
        }
      }),
    // 3. lose the digits printed under the bars (the value is still scannable)
    () => {
      hri = false;
    },
    // 4. last resort: drop trailing content rows
    () => {
      while (lines.length > 1 && needed() > available) lines.pop();
    },
  ];
  for (const step of shrinkSteps) {
    if (needed() <= available) break;
    step();
  }

  const barH = Math.max(MIN_BAR, Math.min(MAX_BAR, available - textH() - (hri ? readableH : 0)));

  // 2-up (or 3-up) stock: SIZE covers the whole media width and each column is
  // drawn at its own x offset, so one PRINT fills a full row of labels.
  const across = Math.max(1, Math.min(3, Math.round(spec.across ?? 1)));
  const columnGap = Math.round((spec.columnGapMm ?? 2) * DOTS_PER_MM);
  const columnDots = spec.widthMm * DOTS_PER_MM;
  const mediaWidthMm = spec.widthMm * across + (spec.columnGapMm ?? 2) * (across - 1);

  const cmds: string[] = [
    `SIZE ${mediaWidthMm} mm,${spec.heightMm} mm`,
    `GAP ${spec.gapMm ?? 2} mm,0 mm`,
    'DIRECTION 1',
    'CLS',
  ];

  let y = marginTop;
  const emitText = (x: number) => {
    lines.forEach((l) => {
      const m = l.scale ?? 1;
      cmds.push(`TEXT ${x},${y},"${l.font}",0,${m},${m},"${l.text}"`);
      y += l.height;
    });
  };
  const emitBarcode = (x: number) => {
    // CODE128, human-readable centered below the bars.
    cmds.push(`BARCODE ${x},${y},"128",${barH},${hri ? 2 : 0},0,2,2,"${clean(spec.code)}"`);
    y += barH + (hri ? readableH : 0);
  };

  for (let col = 0; col < across; col += 1) {
    const x = col * (columnDots + columnGap) + margin;
    y = marginTop;
    if (spec.position === 'Top') {
      emitBarcode(x);
      emitText(x);
    } else {
      emitText(x);
      emitBarcode(x);
    }
  }

  // Each printed row yields `across` labels, so a request for N labels needs
  // ceil(N / across) rows.
  cmds.push(`PRINT 1,${Math.max(1, Math.ceil(spec.copies / across))}`);
  return new TextEncoder().encode(cmds.join('\r\n') + '\r\n');
}

// One 35x15mm test label to verify the label printer connection.
export function buildTestLabelTspl(storeName: string): Uint8Array {
  return buildLabelTspl({
    widthMm: 35,
    heightMm: 15,
    position: 'Bottom',
    header: storeName || 'EPOS',
    name: 'TEST LABEL',
    rows: [],
    code: '1234567890',
    copies: 1,
  });
}
